import { useEffect, useRef } from "react";
import { FaPlay, FaPause, FaStepForward, FaStepBackward } from "react-icons/fa";
import useMethodStore, { useMethodActions } from "@/store/MethodStore";
import ContextContainer from "../ContextContainer";
import { MdOpenInNew } from "react-icons/md";
import { ContextMenuItem } from "../ui/context-menu";
import { useMusicPlayer } from "@/hooks/useMusicPlayer";
import useSettingsStore from "@/store/SettingsStore";
import useMusicStore, { useMusicActions } from "@/store/MusicStore";

const MusicWidget = () => {
  const { setFocusedWindow } = useMethodActions()

  const musicState = useMethodStore(state => state.musicState)
  const widgets = useSettingsStore(state => state.widgets)

  const { setMusicState } = useMethodActions()
  const { setPlayerRef } = useMusicActions()
  const songIndex = useMusicStore(state => state.songIndex)
  const isPlaying = useMusicStore(state => state.isPlaying)
  const isMuted = useMusicStore(state => state.isMuted)
  const isSongFetching = useMusicStore(state => state.isSongFetching)
  const musicHistory = useMusicStore(state => state.musicHistory)
  const progress = useMusicStore(state => state.progress)

  const {
    playNext,
    playPrev,
    handleSeek,
    togglePlayPause,
    fetchRandomSong,
  } = useMusicPlayer()

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    fetchRandomSong();
    if (audioRef) setPlayerRef(audioRef)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPlayerRef])

  return (
    <ContextContainer trigger={
      <div className="relative rounded-xl space-x-4 overflow-hidden flex items-center justify-center p-4 bg-gray-300/95 backdrop-blur-md text-zinc-900 dark:bg-gray-800/95 dark:text-white w-full">
        {/* Show loading state or song details */}
        {!musicHistory[songIndex] ? (
          isSongFetching
            ?   // Skeleton Loader
            <>
              <div className="w-36 h-24 rounded-md bg-gray-700 animate-pulse"></div>

              {/* Song Info and Controls */}
              <div className="w-full">
                <div className="flex justify-between items-center w-full">
                  {/* Song Title and Artist */}
                  <div className="space-y-2">
                    <div className="h-8 w-20 bg-gray-700 animate-pulse rounded-md"></div>
                    <div className="h-4 w-12 bg-gray-700 animate-pulse rounded-md"></div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-gray-700 animate-pulse rounded-full"></div>
                    <div className="h-8 w-8 bg-gray-700 animate-pulse rounded-full"></div>
                    <div className="h-8 w-8 bg-gray-700 animate-pulse rounded-full"></div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="absolute -top-3 left-0 w-full px-0">
                  <div className="w-full h-1 bg-gray-700 animate-pulse rounded-md"></div>
                </div>
              </div>
            </>
            : (<p className="text-zinc-900 dark:text-zinc-100 py-24">No song loaded. Search for a song!</p>)
        ) : (
          <>
            {/* Song Information */}
            <img
              src={musicHistory[songIndex].cover}
              alt={musicHistory[songIndex].title}
              className="w-24 h-24 rounded-md"
            />

            <div className="w-full">
              <div className="flex justify-between items-center w-full">

                <div className="">
                  <h3 className="text-lg font-semibold line-clamp-1">{musicHistory[songIndex].title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{musicHistory[songIndex].artist}</p>
                </div>

                {/* Controls */}
                <div className="flex items-center space-x-3">
                  <button
                    onClick={playPrev}
                    className="text-zinc-900 dark:text-zinc-100 hover:text-blue-600 dark:hover:text-blue-500 transition duration-200"
                  >
                    <FaStepBackward size={20} />
                  </button>
                  <button
                    onClick={togglePlayPause}
                    className="text-zinc-900 dark:text-zinc-100 hover:text-blue-600 dark:hover:text-blue-500 transition duration-200"
                  >
                    {isPlaying ? <FaPause size={20} /> : <FaPlay size={20} />}
                  </button>
                  <button
                    onClick={playNext}
                    className="text-zinc-900 dark:text-zinc-100 hover:text-blue-600 dark:hover:text-blue-500 transition duration-200"
                  >
                    <FaStepForward size={20} />
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="absolute -top-3 left-0 w-full px-0">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={handleSeek}
                  className="w-full h-1 overflow-hidden cursor-pointer"
                />
              </div>

            </div>
          </>
        )}

        {/* Hidden audio element */}
        {musicHistory[songIndex] && (musicState !== "closed" || widgets.includes("Music")) && <audio muted={isMuted} autoPlay={isPlaying} ref={audioRef} src={musicHistory[songIndex].url} />}
      </div>
    }>
      <ContextMenuItem onClick={() => { setMusicState("floating"); setFocusedWindow("Music") }}>
        <MdOpenInNew className="text-lg" />
        <span className="ml-3">Open app</span>
      </ContextMenuItem>
    </ContextContainer>
  );
};

export default MusicWidget;